import { Menu, MenuButton, MenuItem, MenuItems, Transition } from '@headlessui/react'
import { useState } from 'react'
import { usePage, router } from '@inertiajs/react'
import { cx } from '@/utils/strings'
import { getPdfPath } from '@/utils/print'
import { useLocalStorage } from 'usehooks-ts'
import Routes from '@/constants/routes'
import { useTranslations, useLocale, localizedPath } from '@/utils/locale'

interface CopyPageDropdownProps {
  /** Optional className for the container */
  className?: string
}

/**
 * Extract slug from a help article URL
 */
export const extractSlugFromUrl = (url: string): string | null => {
  const pathMatch = url.match(/\/help\/([^/]+)\/?/)
  return pathMatch ? pathMatch[1] : null
}

/**
 * Copy the markdown content of the current help article to clipboard
 */
export const copyPageAsMarkdown = async (url: string, locale: string): Promise<boolean> => {
  try {
    const slug = extractSlugFromUrl(url)
    if (!slug) {
      throw new Error('Could not extract slug from URL')
    }

    // Build localized markdown URL
    const markdownPath = localizedPath(`/help/${slug}.md`, locale)
    const response = await fetch(markdownPath)

    if (!response.ok) {
      throw new Error('Failed to fetch markdown')
    }

    const markdownContent = await response.text()
    await navigator.clipboard.writeText(markdownContent)

    return true
  } catch (error) {
    console.error('Failed to copy markdown:', error)

    // Fallback: copy current page URL
    try {
      await navigator.clipboard.writeText(`${window.location.origin}${url}`)
      return true
    } catch (fallbackError) {
      console.error('Failed to copy fallback URL:', fallbackError)
      return false
    }
  }
}

/**
 * Get the localized markdown URL for a help article
 */
export const getMarkdownUrl = (url: string, locale: string): string => {
  const slug = extractSlugFromUrl(url)
  return slug ? localizedPath(`/help/${slug}.md`, locale) : '#'
}

export default function CopyPageDropdown({ className }: CopyPageDropdownProps) {
  const [copied, setCopied] = useState(false)
  const { url } = usePage()
  const { t } = useTranslations()
  const { currentLocale } = useLocale()
  const pdfPath = getPdfPath(url)
  const [, setSmartSearchQuery] = useLocalStorage<string | undefined>('smartSearchQuery', undefined)

  const handleCopyMarkdown = async () => {
    const success = await copyPageAsMarkdown(url, currentLocale)
    if (success) {
      setCopied(true)
      setTimeout(() => setCopied(false), 2000)
    }
  }

  const openAIChat = () => {
    const slug = extractSlugFromUrl(url)
    if (!slug) return

    const markdownUrl = `${Routes.hostname}${localizedPath(`/help/${slug}.md`, currentLocale)}`

    // Store prompt using useLocalStorage hook (same as SmartSearchSuggestion)
    const prompt = t('Read from ${url} so I can ask questions about it.').replace('${url}', markdownUrl)
    setSmartSearchQuery(prompt)

    // Navigate to smart search page using Inertia router
    router.visit(localizedPath('/smart-search/', currentLocale))
  }

  // Extract slug for building URLs
  const markdownUrl = getMarkdownUrl(url, currentLocale)

  return (
    <div className={cx('relative flex', className)}>
      {/* Main Copy Button */}
      <button
        onClick={handleCopyMarkdown}
        className={cx(
          'relative rounded-l-full text-center font-medium z-0 flex items-center justify-center',
          'text-sm px-3 py-1.5 w-32', // Match Contact support button height but fixed width
          'bg-primaryBlue text-white dark:text-secondary-800',
          'hover:bg-primary-700 hover:dark:bg-primary-100',
          'transition-colors duration-200',
          'focus:outline-none', // Remove focus ring entirely
          'border border-primaryBlue border-r-0', // Remove right border to connect with dropdown
          '!transform-none !scale-100', // Force prevent any scaling
          copied && 'bg-green-600 border-green-600 hover:bg-green-700 active:bg-green-700'
        )}
        style={{ transform: 'none', scale: '1' }} // Inline style to override any global scaling
      >
        <span className="font-bwi text-sm mr-2">&#xe905;</span>
        <span className="whitespace-nowrap">
          {copied ? t('Copied!') : t('Copy for LLM')}
        </span>
      </button>

      {/* Dropdown Arrow Button */}
      <Menu>
        <MenuButton
          className={cx(
            'relative rounded-r-full text-center font-medium z-0 flex items-center justify-center',
            'text-sm px-2 py-1.5', // Match height with smaller padding for arrow
            'bg-primaryBlue text-white dark:text-secondary-800',
            'hover:bg-primary-700 hover:dark:bg-primary-100',
            'transition-colors duration-200',
            'focus:outline-none', // Remove focus ring entirely
            'border border-primaryBlue border-l-primary-700',
            '!transform-none !scale-100' // Force prevent any scaling
          )}
          style={{ transform: 'none', scale: '1' }} // Inline style to override any global scaling
        >
          <span className="font-bwi text-sm">&#xe92d;</span> {/* Chevron down */}
        </MenuButton>

        <Transition
          enter="transition ease-out duration-100"
          enterFrom="opacity-0 scale-95"
          enterTo="opacity-100 scale-100"
          leave="transition ease-in duration-75"
          leaveFrom="opacity-100 scale-100"
          leaveTo="opacity-0 scale-95"
        >
          <MenuItems
            anchor="bottom end"
            className={cx(
              'mt-2 w-80 rounded-lg bg-white dark:bg-secondary-900',
              'border border-secondary-200 dark:border-secondary-700 shadow-lg',
              'focus:outline-none z-20'
            )}
          >
            <div className="px-1 py-1">
              <MenuItem>
                {({ focus }) => (
                  <button
                    onClick={handleCopyMarkdown}
                    className={cx(
                      'group flex w-full items-center rounded-md px-3 py-2 text-sm transition-colors',
                      focus
                        ? 'bg-primaryBlue text-white'
                        : 'text-secondary-900 dark:text-secondary-50'
                    )}
                  >
                    <span className={cx(
                      'mr-3 h-5 w-5 font-bwi text-base flex items-center justify-center',
                      focus ? 'text-white' : 'text-secondary-400 dark:text-secondary-300'
                    )}>
                      &#xe905; {/* Copy icon */}
                    </span>
                    <div className="flex flex-col items-start">
                      <span className="font-medium">{t('Copy for LLM')}</span>
                      <span className={cx(
                        'text-xs',
                        focus ? 'text-blue-100' : 'text-secondary-500 dark:text-secondary-300'
                      )}>
                        {t('Copy page as Markdown for LLMs')}
                      </span>
                    </div>
                  </button>
                )}
              </MenuItem>
            </div>

            <div className="px-1 py-1">
              <MenuItem>
                {({ focus }) => (
                  <button
                    onClick={openAIChat}
                    className={cx(
                      'group flex w-full items-center rounded-md px-3 py-2 text-sm transition-colors',
                      focus
                        ? 'bg-primaryBlue text-white'
                        : 'text-secondary-900 dark:text-secondary-50'
                    )}
                  >
                    <span className={cx(
                      'mr-3 h-5 w-5 font-bwi text-base flex items-center justify-center',
                      focus ? 'text-white' : 'text-secondary-400 dark:text-secondary-300'
                    )}>
                      &#xe990; {/* Chat icon */}
                    </span>
                    <div className="flex flex-col items-start">
                      <span className="font-medium">{t('Open AI Chat')}</span>
                      <span className={cx(
                        'text-xs',
                        focus ? 'text-blue-100' : 'text-secondary-500 dark:text-secondary-300'
                      )}>
                        {t('Ask questions about this page')}
                      </span>
                    </div>
                  </button>
                )}
              </MenuItem>

              <MenuItem>
                {({ focus }) => (
                  <a
                    href={markdownUrl}
                    target="_blank"
                    rel="noopener noreferrer"
                    className={cx(
                      'group flex w-full items-center rounded-md px-3 py-2 text-sm transition-colors',
                      focus
                        ? 'bg-primaryBlue text-white'
                        : 'text-secondary-900 dark:text-secondary-50'
                    )}
                  >
                    <span className={cx(
                      'mr-3 h-5 w-5 font-bwi text-base flex items-center justify-center',
                      focus ? 'text-white' : 'text-secondary-400 dark:text-secondary-300'
                    )}>
                      &#xe94F; {/* File text icon */}
                    </span>
                    <div className="flex flex-col items-start">
                      <span className="font-medium">{t('Open page as Markdown')}</span>
                      <span className={cx(
                        'text-xs',
                        focus ? 'text-blue-100' : 'text-secondary-500 dark:text-secondary-300'
                      )}>
                        {t('View markdown in new tab')}
                      </span>
                    </div>
                  </a>
                )}
              </MenuItem>
            </div>

            <div className="px-1 py-1">
              <MenuItem>
                {({ focus }) => (
                  <a
                    href={pdfPath}
                    target="_blank"
                    rel="noopener noreferrer"
                    className={cx(
                      'group flex w-full items-center rounded-md px-3 py-2 text-sm transition-colors',
                      focus
                        ? 'bg-primaryBlue text-white'
                        : 'text-secondary-900 dark:text-secondary-50'
                    )}
                  >
                    <span className={cx(
                      'mr-3 h-5 w-5 font-bwi text-base flex items-center justify-center',
                      focus ? 'text-white' : 'text-secondary-400 dark:text-secondary-300'
                    )}>
                      &#xe94e; {/* PDF download icon */}
                    </span>
                    <div className="flex flex-col items-start">
                      <span className="font-medium">{t('Download as PDF')}</span>
                      <span className={cx(
                        'text-xs',
                        focus ? 'text-blue-100' : 'text-secondary-500 dark:text-secondary-300'
                      )}>
                        {t('Save page as PDF document')}
                      </span>
                    </div>
                  </a>
                )}
              </MenuItem>
            </div>
          </MenuItems>
        </Transition>
      </Menu>
    </div>
  )
}
